import { memo, useCallback, useEffect, useRef, useState } from "react";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { IconButton } from "@mui/material";
import {
  ButtonComponent,
  OtpCountdownComponent,
  PinInputComponent,
} from "@zenith/components";
import {
  ProfileUpdateApiService,
  RequestEmailUpdateOtpDTO,
  RequestPhoneUpdateOtpDTO,
  VerifyUpdateOtpDTO,
} from "@zenith/services/user";

const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 3 * 60;

export type ProfileUpdatePurpose = "UPDATE_EMAIL" | "UPDATE_PHONE";

export interface ProfileUpdateOtpProps {
  newValue: string;
  purpose: ProfileUpdatePurpose;
  onVerified: (updateToken: string) => void;
  onGoBack: () => void;
}

const ProfileUpdateOtpComponent = memo(function ProfileUpdateOtpComponent({
  newValue,
  purpose,
  onVerified,
  onGoBack,
}: ProfileUpdateOtpProps) {
  const isEmail = purpose === "UPDATE_EMAIL";
  const title = isEmail ? "Email Address Update" : "Telephone Number Update";

  const [pin, setPin] = useState<Array<string | number>>(
    Array(OTP_LENGTH).fill("")
  );
  const [maskedValue, setMaskedValue] = useState<string>("");
  const [seconds, setSeconds] = useState(OTP_EXPIRY_SECONDS);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLocked, setIsLocked] = useState(false);
  const [unavailablePlatforms, setUnavailablePlatforms] = useState<Set<string>>(
    new Set()
  );

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pinString = pin.join("");
  const isPinComplete = pinString.replace(/\s/g, "").length === OTP_LENGTH;

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSeconds(OTP_EXPIRY_SECONDS);
    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const sendOtp = useCallback(
    (
      platform: RequestPhoneUpdateOtpDTO.platform = RequestPhoneUpdateOtpDTO
        .platform.SMS
    ) => {
      setIsSending(true);
      setErrorMessage("");

      const call = isEmail
        ? ProfileUpdateApiService.requestEmailUpdateOtp({
            newEmail: newValue,
            channel: RequestEmailUpdateOtpDTO.channel.WEB,
            countryCode: RequestEmailUpdateOtpDTO.countryCode.NG,
          })
        : ProfileUpdateApiService.requestPhoneUpdateOtp({
            newPhoneNumber: newValue,
            platform,
            channel: RequestPhoneUpdateOtpDTO.channel.WEB,
            countryCode: RequestPhoneUpdateOtpDTO.countryCode.NG,
          });

      call
        .then((response) => {
          if (response.data?.email) setMaskedValue(response.data.email);
          startTimer();
        })
        .catch((error) => {
          const code = error?.body?.internalCode ?? "";
          const apiMessage = error?.body?.message ?? "";
          if (code === "ZENZ-USER-005") {
            setUnavailablePlatforms((prev) => new Set([...prev, platform]));
            setErrorMessage(
              apiMessage ||
                "Resend limit reached for this channel. Try a different option."
            );
          } else {
            setErrorMessage(
              apiMessage || "Failed to send OTP. Please try again."
            );
          }
        })
        .finally(() => setIsSending(false));
    },
    [isEmail, newValue, startTimer]
  );

  useEffect(() => {
    sendOtp();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPinChanged = (pinEntry: number | string, index: number) => {
    const updated = [...pin];
    updated[index] = pinEntry;
    setPin(updated);
  };

  const handleVerify = () => {
    if (!isPinComplete || isVerifying || isLocked) return;
    setIsVerifying(true);
    setErrorMessage("");

    const verifyCall = isEmail
      ? ProfileUpdateApiService.verifyEmailUpdateOtp({
          verificationCode: pinString,
          purpose: VerifyUpdateOtpDTO.purpose.UPDATE_EMAIL,
          channel: VerifyUpdateOtpDTO.channel.WEB,
        })
      : ProfileUpdateApiService.verifyPhoneUpdateOtp({
          verificationCode: pinString,
          purpose: VerifyUpdateOtpDTO.purpose.UPDATE_PHONE,
          channel: VerifyUpdateOtpDTO.channel.WEB,
        });

    verifyCall
      .then((response) => {
        const token = response.data?.updateToken;
        if (token) onVerified(token);
      })
      .catch((error) => {
        const code = error?.body?.internalCode ?? "";
        const apiMessage = error?.body?.message ?? "";
        if (code === "ZENZ-USER-028") {
          setIsLocked(true);
        }
        setErrorMessage(apiMessage || "Verification failed. Please try again.");
        setPin(Array(OTP_LENGTH).fill(""));
      })
      .finally(() => setIsVerifying(false));
  };

  const handleResend = (
    platform: RequestPhoneUpdateOtpDTO.platform = RequestPhoneUpdateOtpDTO
      .platform.SMS
  ) => {
    setIsLocked(false);
    setPin(Array(OTP_LENGTH).fill(""));
    setErrorMessage("");
    sendOtp(platform);
  };

  const displayValue = maskedValue || newValue;

  return (
    <div className="flex flex-col">
      <h5 className="mb-6 text-xl font-medium text-neutral-900">{title}</h5>
      <p className="mb-4 text-base font-medium text-neutral-900">
        OTP Verification
      </p>

      <div className="mb-16 flex items-center gap-2 text-sm text-neutral-900">
        <CheckCircleOutlineOutlinedIcon fontSize="small" />
        <span>
          {isSending
            ? "Sending OTP..."
            : `An OTP has been sent to ${displayValue}`}
        </span>
      </div>

      <PinInputComponent
        id="profile-update-otp"
        label="Enter code here"
        labelClassName="mb-1 text-neutral-500 text-sm"
        pinLength={OTP_LENGTH}
        pin={pin}
        type="tel"
        masked={false}
        pinContainerClass="gap-2 md:gap-7"
        onPinChanged={onPinChanged}
        onBlur={() => void 0}
        hasError={!!errorMessage}
        isValidating={false}
        validationMessage={errorMessage}
      />

      {seconds > 0 && (
        <div className="mt-8 flex justify-center text-base font-normal text-neutral-600">
          <OtpCountdownComponent seconds={seconds} />
        </div>
      )}

      {seconds === 0 && (
        <div className="mt-8 flex flex-col items-center text-base font-normal text-neutral-500">
          <span>Didn&apos;t receive an OTP?</span>
          <button
            type="button"
            className="mt-1 cursor-pointer border-0 bg-transparent p-0 font-normal text-neutral-900 underline"
            onClick={() => handleResend()}
            disabled={isSending || isVerifying}
          >
            Resend OTP
          </button>
        </div>
      )}

      {!isEmail && (seconds === 0 || !!errorMessage) && (
        <div className="mt-8">
          <div className="mb-6 flex items-center gap-2">
            <div className="h-px flex-1 bg-gray-300" />
            <span className="text-sm text-neutral-500">
              Choose another option
            </span>
            <div className="h-px flex-1 bg-gray-300" />
          </div>
          <div className="flex justify-center gap-10">
            {!unavailablePlatforms.has(
              RequestPhoneUpdateOtpDTO.platform.WHATSAPP
            ) && (
              <div className="flex flex-col items-center">
                <IconButton
                  aria-label="whatsapp"
                  size="large"
                  className="hover:bg-gray-300"
                  onClick={() =>
                    handleResend(RequestPhoneUpdateOtpDTO.platform.WHATSAPP)
                  }
                  disabled={isSending || isVerifying}
                >
                  <WhatsAppIcon fontSize="inherit" />
                </IconButton>
                <span className="text-sm text-zinc-900">Whatsapp</span>
              </div>
            )}
            {!unavailablePlatforms.has(
              RequestPhoneUpdateOtpDTO.platform.VOICE_MESSAGE
            ) && (
              <div className="flex flex-col items-center">
                <IconButton
                  aria-label="phonecall"
                  size="large"
                  className="hover:bg-gray-300"
                  onClick={() =>
                    handleResend(
                      RequestPhoneUpdateOtpDTO.platform.VOICE_MESSAGE
                    )
                  }
                  disabled={isSending || isVerifying}
                >
                  <PhoneInTalkOutlinedIcon fontSize="inherit" />
                </IconButton>
                <span className="text-sm text-zinc-900">Phone call</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-14 flex gap-4">
        <ButtonComponent
          id="back-button"
          label="Back"
          onClick={onGoBack}
          size="large"
          type="secondary"
          fullWidth
          disabled={isVerifying}
        />
        <ButtonComponent
          id="continue-button"
          label="Continue"
          onClick={handleVerify}
          size="large"
          type="primary"
          fullWidth
          disabled={!isPinComplete || isVerifying || isLocked}
          isLoading={isVerifying}
        />
      </div>
    </div>
  );
});

export default ProfileUpdateOtpComponent;
